import * as React from 'react';

import {
  FormControl,
  FormLabel,
  RadioGroup,
  RadioGroupProps,
  FormControlLabel,
  Radio,
  FormHelperText,
} from '@mui/material';

import _startCase from 'lodash/startCase';

import { FieldValues, UseControllerProps } from 'react-hook-form';

import ReadOnlyField from './ReadOnlyField';
import EditableField from './EditableField';
import { useFormUpdateProxy } from '../core/context/FormUpdateProxyContext';
import useEditableController from '../core/api/hooks/common/useEditableController';

interface IControlledRadioGroup<TFieldValues extends FieldValues> extends UseControllerProps<TFieldValues> {
  label?: string;
  readOnly?: boolean;
  disableEditPopper?: boolean,
  radioOptions: { value: string | boolean | undefined, label: string }[];
  RadioGroupProps?: RadioGroupProps;
  isEditable?: boolean;
  onToggle?: () => void;
}

function ControlledRadioGroup<TFieldValues extends FieldValues>({
  name,
  label,
  readOnly,
  disableEditPopper,
  control,
  rules,
  radioOptions,
  RadioGroupProps: radioGroupProps,
  isEditable = true,
  onToggle,
}: IControlledRadioGroup<TFieldValues>) {
  const { requestUpdate } = useFormUpdateProxy();
  const {
    field,
    fieldState,
    revertChanges,
    commitChanges,
  } = useEditableController<TFieldValues>({
    name,
    control,
    defaultValue: undefined,
  });
  return (
    <EditableField
      fieldName={name}
      renderMode={readOnly ? 'read' : 'edit'}
      disableEditPopper={disableEditPopper}
      saveSuccess={() => {
        commitChanges();
        requestUpdate();
      }}
      onEditCancel={revertChanges}
      renderEditField={() => (
        <FormControl component="fieldset" error={fieldState.invalid}>
          <FormLabel component="legend">
            {`${label || _startCase(name)}${rules?.required === true ? ' *' : ''}`}
          </FormLabel>
          <RadioGroup
            {...radioGroupProps}
            value={field.value || ''}
            onChange={field.onChange}
          >
            {radioOptions.map((radio) => (
              <FormControlLabel
                key={typeof radio.value === 'string' ? radio.value : radio.label}
                control={(
                  <Radio
                    disabled={!isEditable}
                    inputProps={{
                      onClick: () => {
                        if (onToggle) {
                          onToggle();
                        }
                      },
                    }}
                  />
)}
                label={radio.label}
                value={radio.value}
              />
            ))}
          </RadioGroup>
          {fieldState.invalid && fieldState.error && (
            <FormHelperText>{fieldState.error.message}</FormHelperText>
          )}
        </FormControl>
      )}
      renderReadField={() => (
        <ReadOnlyField
          name={label || _startCase(name)}
          value={field.value}
        />
      )}
    />
  );
}

export default ControlledRadioGroup;
